
import * as THREE from 'three';
import React, { useRef, useState, useEffect, useRef as useRefLocal, Suspense } from 'react';
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber';
import {
  Html, useProgress,
  Image,
  Text,
  Environment,
  ScrollControls,
  useScroll,
  useTexture,
} from '@react-three/drei';
import { easing } from 'maath';
import { shaderMaterial } from '@react-three/drei';
import './util';
import FloatingCardOverlay from './FloatingCardOverlay';
import { useSpring, animated } from '@react-spring/web';
// import { floatingCardContents } from './Card'; // or wherever you defined it

// Custom HDR background mesh with contrast boost
import { useLoader } from '@react-three/fiber';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';

function HDRBackground() {
  // === EASY HANDLES: Adjust these values ===
  const BRIGHTNESS = -0.01; // Lower = darker, Higher = brighter
  const CONTRAST = 2.1;     // 1 = normal, >1 = more contrast
  // ========================================
  const texture = useLoader(RGBELoader, 'hdr/sky-tiny.hdr');
  texture.mapping = THREE.EquirectangularReflectionMapping;
  // Save/restore fog for this mesh only
  const handleBeforeRender = (renderer, scene, camera, geometry, material, group) => {
    const oldFog = scene.fog;
    scene.fog = null;
    material.onAfterRender = () => {
      scene.fog = oldFog;
      material.onAfterRender = null;
    };
  };
  return (
    <mesh scale={[100, 100, -100]} rotation={[0, Math.PI, 0]} onBeforeRender={handleBeforeRender}>
      <sphereGeometry args={[1, 64, 64]} />
      <meshBasicMaterial
        map={texture}
        side={THREE.BackSide}
        toneMapped={false}
        onBeforeCompile={shader => {
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <map_fragment>',
            `#include <map_fragment>\n// Brightness/Contrast\nfloat contrast = ${'CONTRAST'};\nfloat brightness = ${'BRIGHTNESS'};\nvec3 avg = vec3(0.5);\ndiffuseColor.rgb = (diffuseColor.rgb - avg) * contrast + avg + brightness;`
          ).replace('CONTRAST', CONTRAST).replace('BRIGHTNESS', BRIGHTNESS);
        }}
      />
    </mesh>
  );
}

// --- NEW: Loader component for Suspense fallback ---
function Loader() {
  const { progress } = useProgress()            // ← this blows up: no Canvas context available
  return (
    <Html center>
      Loading… {Math.round(progress)}%
    </Html>
  )
}

// Constants
const RADIUS = 1.4 * 3;
const COUNT = 7;
const CARD_HEIGHT = 2.25;

// Styles for scroll buttons and container
const btnStyle = {
  background: '#eee',
  border: 'none',
  borderRadius: '6px',
  padding: '16px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '18px',
  minWidth: '48px',
  minHeight: '48px',
  lineHeight: '1',
};

const scrollStyle = {
  width: '340px',
  height: '330px',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  backgroundColor: 'rgba(255,255,255,0.85)',
  padding: '12px',
  borderRadius: '8px',
  boxShadow: '0 0 10px rgba(0,0,0,0.3)',
  fontFamily: 'sans-serif',
  fontSize: '14px',
  color: '#111',
  pointerEvents: 'auto',
  position: 'relative',
};

const floatingCardContents = {
  About: (
    <>
      <p><strong>Henry Simon - Owner</strong></p>
      <p> Full-stack engineering professional with 9 years of experience consulting & architecting advanced systems integrations, network monitoring tools, databases, and cloud-based web applications for United States telecommunications industry. </p>
      <p> Specialized experience developing laboratory test & management tools for internet backbone subsystems, video streaming applications, and enterprise cybersecurity. </p>
      
    </>
  ),
  Reading: (
    <>
      <p><strong>Articles</strong></p>
      {[
        'Prompt tools - https://github.com/f/awesome-chatgpt-prompts',
        'Is my toddler a stochastic parrot? - https://archive.is/AUOPt',
        'Coloring Computers - https://ipfs.io/ipfs/QmYz7DPRWypGQcbAHr7Mi8EKB6ntSPsEnUsCXbAhBiHQZP/',
        'Language Ontology Encyclopedia - https://glottolog.org',
        'Prompt Testing - https://github.com/promptfoo/promptfoo',
      ].map((entry, i) => {
        const [title, url] = entry.split(' - ');
        return (
          <p key={i}>
            {title} - <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
          </p>
        );
      })}
    </>
  ),
  Education: (
    <>
      <p><strong>School</strong></p>
      <p> Evergreen State College, Bachelor of Computer Science </p>
      <p> Revature, Full Stack development program </p>
      <p><strong>Leadership Experience</strong></p>
      <p> Oregon Ducks Marching Band - Touring Percussionist for two NCAA football seasons </p>
      <p> Summer Camp Counselor & Lifeguard - Camp Namanu, outdoor wilderness in Sandy River, OR </p>
      <p> Teaching Aide - University Computer Science and Hardware/Robotics students </p>
      <p> Food Service - Barista </p>
    </>
  ),
  Media: (
    <>
      <p><strong>Soundcloud</strong></p>
      <p> <a href="https://soundcloud.com/eternal1st">Soundcloud</a> </p>
      <p><strong>LinkedIn</strong></p>
      <p> <a href="https://www.linkedin.com/in/henry-simon-374904153">LinkedIn</a> </p>
      <p><strong>Studio Diagrams</strong></p>
      <p> TBA </p>
      <p><strong>Blog</strong></p>
      <p> TBA </p>
    </>
  ),
  Technologies: (
    <>
      <p><strong>Languages & Artifact Management</strong></p>
      <ul>
        <p> Python, PIP </p>
        <p> Ruby, RubyGems </p>
        <p> Java, Maven, Gradle </p>
        <p> Javascript, Typescript, NPM, Yarn </p>
        <p> JSON, XML, YAML </p>
        <p> HTML, CSS </p>
        <p> Bash, Osascript </p>
        <p> Apt, Yum, Brew, and similar Unix package managers. </p>
        <p> CMD, PowerShell, WSL, Chocolatey</p>
      </ul>

      <p><strong>Devops & Source Management</strong></p>
      <ul>
        <p> Jenkins </p>
        <p> Gitlab CI </p>
        <p> Git, Github, Gitlab, Bitbucket, Stash </p>
        <p> Docker </p>
        <p> Artifactory </p>
        <p> Cron, Task Scheduler </p>
        <p> Kubernetes </p>
        <p> Rancher </p>
      </ul>

      <p><strong>Network & Security</strong></p>
      <ul>
        <p> Infoblox, Netbox, and similar SDN / IPAM tools. </p>
        <p> Ufw, AWS security console, and other firewall tools. </p>
        <p> TraceRoute, Ping, Telnet, Dig, Nslookup, Nmap, and similar network deep-diagnostic tools  </p>
        <p> CharlesProxy </p>
        <p> Qualys Scanning and Kenna reporting </p>
        <p> Tanium Sensor and Agent scripting </p>
        <p> OAuth, ActiveDirectory, JWT, and other SSO workflows </p>
        <p> OpenSSL, OpenSSH</p>
        <p> HTTPS, SSL, TLS, websocket, and related web server protocol configs </p>
        <p> SSH, SFTP, RDP client configs. </p>
        <p> ELB, Ingress, and DNS implementations via NginX. </p>
        <p> WireShark for packet sniffing.</p>
        <p> Gitleaks, Dependabot, and similar repository scan tools </p>
        <p> SSH, SFTP, RDP client configs. </p>
        <p> CMD, PowerShell, WSL, Chocolatey</p>
      </ul>


      <p><strong>Platform & Infrastructure</strong></p>
      <ul>
        <p> Kubernetes </p>
        <p> Rancher, CaaS technologies </p>
        <p> Docker, Dockerhub </p>
        <p> Ansible, Chef </p>
        <p> NginX, Apache Tomcat </p>
        <p> AWS EC2, RDS, S3, Route53 </p>
        <p> VMWare, VSphere, Virtualbox </p>
      </ul>

      <p><strong>Client Side</strong></p>
      <ul>
        <p> React, VueJS, Angular, and similar JS/TS frameworks. </p>
        <p> Django, Jinja2, templating engines. </p>
        <p> JQuery, AJAX, and vanilla Javascript </p>
        <p> Bootstrap, Material UI, Tailwind CSS libraries </p>
      </ul>

      <p><strong>Server Side</strong></p>
      <ul>
        <p> React, VueJS, Angular, and similar JS/TS frameworks. </p>
        <p> REST APIs, SOAP APIs, Webhooks, GraphQL, Vector queries, HTTP and NON-HTTP Protocols </p>
        <p> Django, Jinja2, templating engines. </p>
        <p> JQuery, AJAX, and vanilla Javascript </p>
        <p> Bootstrap, Material UI, Tailwind CSS libraries </p>
      </ul>

      <p><strong>Database, ORM, RDBMS</strong></p>
      <ul>
        <p> SQL, Postgres, MySQL, Oracle, Redis, MariaDB </p>
        <p> NoSQL, MongoDB </p>
        <p> Hibernate, JDBC </p>
        <p> SQLAlchemy </p>
      </ul>

      <p><strong>IDE & Admin Tools</strong></p>
      <ul>
        <p> Visual Studio Code, Sublime Text, Nano, VIM, PyCharm, IntelliJ </p>
        <p> Postman, Insomnia, Bruno, etc </p>
        <p> Chrome Dev Tools </p>
        <p> Eclipse & Spring Tool Suite </p>
        <p> Chrome Dev Tools </p>
        <p> DBeaver, MySQL Workbench, Oracle Workbench </p>
        <p> Mongo Compass, Studio 3T </p>
        <p> AWS S3 Browser, FileZilla, CyberDuck, and related SFTP clients </p>
        <p> XCode & Android Studio </p>
        <p> Microsoft Office Suite, Google Office Suite </p>
        <p> JIRA, Cherwell for Process Management </p>
        <p> Chalk, Confluence for Knowledge Base </p>
        <p> XCode & Android Studio </p>
        <p> Microsoft Office Suite, Google Office Suite </p>
        <p> Cisco Webex, Webex Bots API </p>
        <p> Slack, Slack Bots API </p>
      </ul>

      <p><strong>Workstation & Host Machines</strong></p>
      <ul>
        <p> Linux / Unix - Debian, Ubuntu, Red Hat, CentOS, Kali, Amazon Linux, etc. </p>
        <p> MacOS </p>
        <p> Windows Server </p>
        <p> Arduino, RaspPI, & embedded systems. </p>
        <p> Cisco, Unifi, Vecima, and CommScope E2E network stacks. </p>
      </ul>
    </>
  ),
  Experience: (
    <>
      <p><strong>CV</strong></p>
      <p> Charter Communications </p>
      <ul><li>software engineer iii</li><li>network engineer iii</li></ul>
      <p> Infosys Ltd </p>
      <ul><li>senior technical consultant</li><li>SDET / data analyst</li></ul>
      <p> Revature </p>
      <ul><li>full stack developer</li></ul>
      <p> Evergreen State College </p>
      <ul><li>computer science tutor</li></ul>
    </>
  ),
  Contact: (
    <>
      <p> henry@hsitek.com </p>
    </>
  ),
};


// Utilities
const rotateAroundYAxis = (angle, radius) => [
  Math.sin(angle) * radius,
  0,
  Math.cos(angle) * radius
];

const labels = {
  '_img1.jpeg': 'Contact',
  '_img2.jpeg': 'About',
  '_img3.jpeg': 'Experience',
  '_img4.jpeg': 'Media',
  '_img5.jpeg': 'Education',
  '_img6.jpeg': 'Reading',
  '_img7.jpeg': 'Technologies'
};

export function App() {
  // Touch swipe state
  const touchStartX = useRef(null);
  const touchActive = useRef(false);

  const [focusIndex, setFocusIndex] = useState(null);
  const [focusPos, setFocusPos] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const overlayDelay = 220; // ms, adjust as desired
  const lastNavTime = useRefLocal(0);
  const scrollControls = useRef();

  const calcPos = (i) => {
    const theta = (i / COUNT) * Math.PI * 2;
    return new THREE.Vector3(...rotateAroundYAxis(theta, RADIUS));
  };

  useEffect(() => {
    const handleKey = (e) => {
      const now = performance.now();
      if (now - lastNavTime.current < 265) return;
      lastNavTime.current = now;

      if (focusIndex !== null) {
        const insideInnerScroll = document.activeElement?.classList?.contains('inner-scroll-container');

        if (e.key === 'Escape') {
          const scrollTarget = focusIndex / COUNT;
          scrollControls.current?.scrollTo(scrollTarget);
          setFocusIndex(null);
          setFocusPos(null);
        } else if ((e.key === 'ArrowLeft' || e.key === 'a')) {
          const prev = (focusIndex - 1 + COUNT) % COUNT;
          setFocusIndex(prev);
          setFocusPos(calcPos(prev));
          scrollControls.current?.scrollTo(prev / COUNT);
        } else if ((e.key === 'ArrowRight' || e.key === 'd')) {
          const next = (focusIndex + 1) % COUNT;
          setFocusIndex(next);
          setFocusPos(calcPos(next));
          scrollControls.current?.scrollTo(next / COUNT);
        } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !insideInnerScroll) {
          e.preventDefault();
        }
      }
    };

    const handleWheel = (e) => {
      const now = performance.now();
      if (now - lastNavTime.current < 265) return;
      lastNavTime.current = now;

      const isInsideHtml = e.composedPath().some((el) =>
        el.classList?.contains?.('inner-scroll-container')
      );

      if (focusIndex !== null && !isInsideHtml) {
        e.preventDefault();
        if (e.deltaY > 0) {
          const next = (focusIndex + 1) % COUNT;
          setFocusIndex(next);
          setFocusPos(calcPos(next));
          scrollControls.current?.scrollTo(next / COUNT);
        } else if (e.deltaY < 0) {
          const prev = (focusIndex - 1 + COUNT) % COUNT;
          setFocusIndex(prev);
          setFocusPos(calcPos(prev));
          scrollControls.current?.scrollTo(prev / COUNT);
        }
      }
    };

    // Touch handlers for single-swipe card navigation
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        touchStartX.current = e.touches[0].clientX;
        touchActive.current = true;
      }
    };
    const handleTouchMove = (e) => {
      // Prevent scroll if a swipe is in progress
      if (touchActive.current) e.preventDefault();
    };
    const handleTouchEnd = (e) => {
      if (!touchActive.current || touchStartX.current === null) return;
      const endX = (e.changedTouches && e.changedTouches[0]?.clientX) || 0;
      const dx = endX - touchStartX.current;
      const threshold = 40; // px, minimum swipe distance
      if (Math.abs(dx) > threshold) {
        const now = performance.now();
        if (now - lastNavTime.current < 265) return;
        lastNavTime.current = now;
        if (focusIndex !== null) {
          if (dx < 0) {
            // Swipe left, go to next card
            const next = (focusIndex + 1) % COUNT;
            setFocusIndex(next);
            setFocusPos(calcPos(next));
            scrollControls.current?.scrollTo(next / COUNT);
          } else {
            // Swipe right, go to previous card
            const prev = (focusIndex - 1 + COUNT) % COUNT;
            setFocusIndex(prev);
            setFocusPos(calcPos(prev));
            scrollControls.current?.scrollTo(prev / COUNT);
          }
        }
      }
      touchActive.current = false;
      touchStartX.current = null;
    };

    window.addEventListener('keydown', handleKey);
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [focusIndex]);

  const handleCardClick = (pos, i) => {
    setFocusIndex(i);
    setFocusPos(pos);
    setShowOverlay(false);
    scrollControls.current?.scrollTo(i / COUNT);
    setTimeout(() => setShowOverlay(true), overlayDelay);
  };

  // Overlay animation: fade and scale in
  const overlaySpring = useSpring({
    opacity: showOverlay && focusIndex !== null ? 1 : 0,
    transform: showOverlay && focusIndex !== null ? 'scale(1)' : 'scale(0.92)',
    config: { tension: 220, friction: 22 },
  });

  // Hide overlay immediately when focusIndex is cleared
  useEffect(() => {
    if (focusIndex === null) setShowOverlay(false);
  }, [focusIndex]);

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
      <Canvas
        camera={{ position: [0, 0, 100], fov: 65 }}
        onPointerMissed={() => {
          if (focusIndex !== null) {
            const scrollTarget = focusIndex / COUNT;
            scrollControls.current?.scrollTo(scrollTarget);
            setFocusIndex(null);
            setFocusPos(null);
          }
        }}
      >
        <Suspense fallback={<Loader />}>
          <fog attach="fog" args={["#07a474", 8.5, 12]} />

          <ScrollControls ref={scrollControls} pages={4} infinite>
            <Rig
              focusIndex={focusIndex}
              focusPos={focusPos}
              scrollControls={scrollControls}
            >
              <Carousel
                onCardClick={handleCardClick}
                focusIndex={focusIndex}
              />
            </Rig>
            <Banner position={[0, -0.1, 0]} />
          </ScrollControls>

          {/* Use Environment for lighting only, not as visible background */}
          <Environment
            files="hdr/sky-tiny.hdr"
            background={true}
            blur={0.0}
            environmentIntensity={1.0}
            rotation={[Math.PI / -2, Math.PI / 6, Math.PI / 3]}
          />
          {/* Custom HDR background with contrast boost */}
          <HDRBackground />
        </Suspense>
      </Canvas>

      <animated.div style={{
        position: 'absolute',
        top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none',
        zIndex: 10,
        ...overlaySpring,
        display: focusIndex !== null ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ pointerEvents: 'auto' }}>
          <FloatingCardOverlay
            focusIndex={focusIndex}
            focusPos={focusPos}
            label={focusIndex !== null ? Object.values(labels)[focusIndex] : null}
            contents={floatingCardContents}
          />
        </div>
      </animated.div>
      </div>
    );
  }

function Rig({ focusIndex, focusPos, scrollControls, ...props }) {
  const ref = useRef();
  const scroll = useScroll();

  useEffect(() => {
    const handler = (e) => {
      if (focusIndex === null && scrollControls.current) {
        if (e.key === 'ArrowLeft' || e.key === 'a') scrollControls.current.scrollTo(scroll.offset - 1 / COUNT);
        if (e.key === 'ArrowRight' || e.key === 'd') scrollControls.current.scrollTo(scroll.offset + 1 / COUNT);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusIndex, scroll.offset]);

  useFrame((state, delta) => {
    const camera = state.camera;
    if (focusIndex !== null) {
      const indexTheta = (focusIndex / COUNT) * Math.PI * 2;
      const groupRot = ref.current.rotation.y;
      const theta = indexTheta + groupRot;
      const desiredRadius = RADIUS + 4;
      const desiredPos = new THREE.Vector3(...rotateAroundYAxis(theta, desiredRadius));
      desiredPos.y = 2.1;
      easing.damp3(camera.position, [desiredPos.x, desiredPos.y, desiredPos.z], 0.5, delta);
    } else {
      ref.current.rotation.y = -scroll.offset * Math.PI * 2;
      state.events.update();
      easing.damp3(camera.position, [-state.pointer.x * 2, state.pointer.y + 1.5, 10], 0.5, delta);
    }

    const bannerMin = 1.6 * 3 + 0.5;
    const r = Math.hypot(camera.position.x, camera.position.z);
    if (r < bannerMin) {
      camera.position.x *= bannerMin / r;
      camera.position.z *= bannerMin / r;
    }

    const center = new THREE.Vector3(0, 0, 0);
    const lookAtMatrix = new THREE.Matrix4().lookAt(camera.position, center, camera.up);
    const targetQuat = new THREE.Quaternion().setFromRotationMatrix(lookAtMatrix);
    camera.quaternion.slerp(targetQuat, delta * 4.0);
  });

  return <group ref={ref} {...props} />;
}

function Carousel({ radius = RADIUS, count = COUNT, onCardClick, focusIndex }) {
  return Array.from({ length: count }, (_, i) => {
    const theta = (i / count) * Math.PI * 2;
    const pos = rotateAroundYAxis(theta, radius);
    const url = `/_img${(i % 10) + 1}.jpeg`;
    const label = Object.entries(labels).find(([key]) => url.includes(key))?.[1];

    return (
      <Card
        key={i}
        index={i}
        url={url}
        position={pos}
        rotation={[0, theta, 0]}
        onClick={onCardClick}
        focusIndex={focusIndex}
        label={label}
      />
    );
  });
}

export function Card({ url, index, onClick, focusIndex, position, label, ...props }) {
  const groupRef = useRef();
  const matRef = useRef();
  const textRef = useRef();
  const [isHovered, setHovered] = useState(false);
  const isFocused = focusIndex === index;

  const handlePointerOver = (e) => (e.stopPropagation(), setHovered(true));
  const handlePointerOut = () => setHovered(false);
  const handleClick = (e) => {
    e.stopPropagation();
    const worldPos = new THREE.Vector3();
    groupRef.current.getWorldPosition(worldPos);
    onClick(worldPos, index);
  };

  const getTargetScale = (focused, hovered) => (focused ? 1.22 : hovered ? 1.15 : 1);

  useFrame(({ camera }, delta) => {
    const targetScale = getTargetScale(isFocused, isHovered);
    easing.damp3(groupRef.current.scale, [targetScale, targetScale, targetScale], 0.15, delta);

    const targetY = isFocused ? 0.8 : 0;
    const [x, , z] = position;
    easing.damp3(groupRef.current.position, [x, targetY, z], 0.1, delta);

    if (textRef.current) {
      textRef.current.position.y = (CARD_HEIGHT * groupRef.current.scale.y) / 2;
      textRef.current.lookAt(camera.position);
    }
  });

  return (
    <group ref={groupRef} {...props}>
      <Text
        ref={textRef}
        fontSize={0.25}
        maxWidth={3}
        anchorX="center"
        anchorY="bottom"
        font="/fonts/Inter-Bold.ttf"
        outlineWidth={0.01}
        outlineColor="black"
      >{label}</Text>

      <Image
        ref={matRef}
        url={url}
        transparent
        side={THREE.DoubleSide}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
        position={[0, 0, 0]}
      >
        <bentPlaneGeometry args={[0.1, 3, CARD_HEIGHT, 20, 20]} />
      </Image>
    </group>
  );
}


// Custom meshSineMaterial for wavy animated banner with strong green gradient tint
const meshSineMaterial = shaderMaterial(
  {
    map: null,
    time: 0,
    opacity: 0.7,
    repeat: [30, 1],
    anisotropy: 16,
    tintA: new THREE.Color('#000000'), // Black
    tintB: new THREE.Color('#07a474'), // Bright green
  },
  // Vertex Shader
  `
    uniform float time;
    varying vec2 vUv;
    void main() {
      vUv = uv;
      vec3 pos = position;
      float freq = 2.0;
      float amp = 0.07;
      pos.y += sin(uv.x * 3.1415 * freq + time) * amp;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
    }
  `,
  // Fragment Shader
  `
    uniform sampler2D map;
    uniform float opacity;
    uniform vec2 repeat;
    uniform vec3 tintA;
    uniform vec3 tintB;
    varying vec2 vUv;
    void main() {
      vec2 uv = vUv * repeat;
      vec4 texColor = texture2D(map, uv);
      // Force a strong green overlay: multiply image color by green gradient
      float grad = smoothstep(0.0, 1.0, vUv.y);
      vec3 tint = mix(tintA, tintB, grad);
      vec3 greened = texColor.rgb * tint * 2.0; // Multiply for strong effect
      gl_FragColor = vec4(greened, texColor.a * opacity);
      if (gl_FragColor.a < 0.01) discard;
    }
  `
);
extend({ meshSineMaterial });

function Banner(props) {
  const ref = useRef();
  const texture = useTexture('/hsitek.jpeg');
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(30, 1);
  texture.anisotropy = 16;
  const scroll = useScroll();
  useFrame((state, delta) => {
    if (ref.current && ref.current.material) {
      ref.current.material.time += Math.abs(scroll.delta) * 4;
      if (ref.current.material.map) {
        ref.current.material.map.offset.x += delta / 2;
      }
    }
  });
  return (
    <mesh ref={ref} {...props}>
      <cylinderGeometry args={[1.6 * 3, 1.6 * 3, 0.14 * 3, 128, 16 * 3, true]} />
      <meshSineMaterial
        map={texture}
        map-anisotropy={16}
        map-repeat={[30, 1]}
        color="#07a474"
        side={THREE.DoubleSide}
        toneMapped={false}
        transparent
        opacity={0.7}
        time={0}
      />
    </mesh>
  );
}